import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiArrowBack, BiBrush, BiSolidShapes } from "react-icons/bi";
import * as Yup from "yup";

import { AlertsType } from "../../../../constants/alerts.constant";
import useComponentVisible from "../../../../hooks/useComponentVisible";
import { BlockType } from "../../../../interfaces/models/Knowledge/scenario.interface";
import AiQC from "../../../common/AiQC";
import HintBlock from "../../../common/HintBlock";
import { createRandomString, isAlphabetic } from "../../../tools";
import DeleteButton from "../DeleteButton";
import NodeParentChanger from "../NodeParentChanger";
import ScenarioHeader from "../ScenarioHeader";
import { MacBotActionKind } from "../types";
import { MacBotButtonSettingForm } from "./scenario-button-setting-form.component";
import ScenarioSide from "./scenario-side";
import { ScenarioSimpleTextInput } from "./scenario-simple-text-input";
import AddNoteButton from "../AddNoteButton";
import NoteForm, { getNoteSchema, INoteForm } from "../NoteForm";
import { v4 as uuidv4 } from "uuid";
import { selectUseId } from "../../../../features/auth/auth.slice";
import { useAppSelector } from "../../../../app/hook";
import MarkdownHelpModal from "../../../ui/MarkdownHelpModal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkEmoji from "remark-emoji";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter/dist/cjs";
import { dracula } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { Pluggable } from "unified";
import remarkBreaks from "remark-breaks";

export const MacBotResponseForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  config,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  config: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
}) => {
  const { t } = useTranslation();
  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
    currentCaretPosition: 0
  });

  const { ref, isComponentVisible } = useComponentVisible(showContextMenu);
  const [cloneJson, setCloneJson] = useState<IResponseForm | null>(null);
  const [showMarkDownGuide, setShowMarkDownGuide] = useState(false);
  const userId = useAppSelector(selectUseId);

  interface IResponseForm extends INoteForm {
    BlockTitle?: string;
    BotResponse: string;
    isAiQcActive?: boolean;
  }

  const schema = Yup.object({
    BlockTitle: Yup.string()
      .required(t(AlertsType.ISKBKO025))
      .max(30, t("Wizard/validationLabels/key16")),
    BotResponse: Yup.string().required(t(AlertsType.ISKBKO026)),
    ...getNoteSchema(t)
  });

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control
  } = useForm<IResponseForm>({
    resolver: yupResolver(schema) as any,
    mode: "onSubmit",
    defaultValues: {
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: "",
          description: "",
          qc: false,
          author: userId
        }
      ]
    }
  });
  const { fields } = useFieldArray({
    control,
    name: "notes"
  });
  //Check Show Button Delete
  useEffect(() => {
    if (itemData) {
      const find = tree.find((dr) => dr.id == itemData.id);
      if (find && find.parent == null) {
        setShowDeleteButton(false);
      }
    }
  }, [tree]);

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);
  const popupAudioPath: { [key: string]: string } = {
    en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716242734_add_bot_message_en.mp3",
    fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716242877_add_bot_message_fr.mp3"
  };

  useEffect(() => {
    reset({
      BlockTitle: itemData?.title ?? "",
      BotResponse: itemData?.message ?? "",
      isAiQcActive: itemData?.isAiQcActive ?? false,
      notes: itemData?.notes
    });

    setCloneJson({
      BlockTitle: itemData?.title ?? "",
      BotResponse: itemData?.message ?? "",
      isAiQcActive: itemData?.isAiQcActive ?? false,
      showNoteForm: itemData?.notes?.length > 0 || false,
      notes: itemData?.notes
    });

    if (!itemData) {
      setShowDeleteButton(false);
    }
  }, [itemData]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies((prev) => prev.filter((p, index) => index !== btnIndex));
  };
  const addButtonHandler = (qBtn: number) => {
    setQuickReplies((prev) => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies((prev) =>
      prev.map((p, index) => (index === _index ? qRep : p))
    );

    setSelectedButtonToEdit(-1);
  };

  const handleQuickReplySelect = (index: number) => {
    setSelectedButtonToEdit(index);
    setShowButtonSetting(true);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  const createTitle = (text: string) => {
    let titleCode = 0;
    tree.forEach((element) => {
      if (element.type === BlockType.Response) {
        const _code = element.title.split("-");
        if (_code[0] && Number(_code[0]) > titleCode) titleCode = +_code[0];
      }
    });

    if (typeof text != "string" || !text) {
      return "";
    }
    let acronym = text
      ?.match(/[\p{Alpha}\p{Nd}]+/gu)
      ?.reduce(
        (previous, next) =>
          previous +
          (+next === 0 || parseInt(next) ? parseInt(next) : next[0] || ""),
        ""
      )
      ?.toUpperCase();

    if (!acronym || (acronym && acronym.length < 3) || !isAlphabetic(acronym)) {
      acronym = createRandomString(5);
    }

    return `${titleCode + 1}-${acronym?.slice(0, 3)}`;
  };

  const ButtonSubmit = (data: IResponseForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.Response,
        title: data.BlockTitle,
        message: data.BotResponse,
        parent: itemData.parent,
        isAiQcActive: data.isAiQcActive,
        notes: data.notes
      };

      if (
        JSON.stringify(cloneJson) ==
        JSON.stringify({
          BlockTitle: data.BlockTitle,
          BotResponse: data.BotResponse,
          isAiQcActive: data.isAiQcActive,
          notes: data.notes
        })
      ) {
        setShow?.(false);
        return;
      }

      changeScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    } else {
      const _data = {
        type: BlockType.Response,
        title: data.BlockTitle,
        message: data.BotResponse,
        parent: parentId,
        isAiQcActive: data.isAiQcActive,
        notes: data.notes
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    }
  };

  const handleRightClick = (event: any) => {
    event.preventDefault();
    setShowContextMenu(true);
    const rect = event.currentTarget.getBoundingClientRect();
    const currentCaretPosition = event.currentTarget.selectionStart;

    setContextMenuPosition({
      top: event.clientY - rect.top,
      left: event.clientX - rect.left,
      currentCaretPosition: currentCaretPosition
    });
  };

  // Function to measure caret's pixel position using a canvas approach
  const measureCaret = (input: any, caretIndex: any) => {
    const rect = input.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  };

  // Function to get the caret position from the event

  const getCaretPosition = (e: any) => {
    const inputElement = e.target;
    const caretIndex = inputElement.selectionStart;

    // Measure the caret's pixel position
    const { top, left } = measureCaret(inputElement, caretIndex);

    // Set the caret position for the popup
    setContextMenuPosition({
      top: e.clientY - top,
      left: e.clientX - left,
      currentCaretPosition: caretIndex
    });

    const value: string = e.target.value;

    if (
      value.endsWith("$:var_") ||
      value.endsWith("$:var_ ") ||
      value.endsWith("/")
    ) {
      setShowContextMenu(true);
    }
  };

  useEffect(() => {
    setShowContextMenu(false);
  }, [isComponentVisible]);

  const onSelectVariable = (node: any) => {
    let value = watch("BotResponse");

    if (node) {
      value = value + " ";
      value = value.replaceAll("/ ", "");
      value = value.replaceAll("$:var_ ", "");

      const updatedValue =
        value.slice(0, contextMenuPosition.currentCaretPosition) +
        ` ${node.variableName} ` +
        value.slice(contextMenuPosition.currentCaretPosition);

      setValue("BotResponse", updatedValue);
      setShowContextMenu(false);
    }
  };

  useEffect(() => {
    if (config?.metaData?.variables) {
      setContextMenuList([...config?.metaData?.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowResponseForm}
          onClose={() => {
            reset();
            setShow?.(false);
          }}
        />

        <div className="tw-w-full tw-h-full tw-px-3 tw-py-2 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex tw-font-[Lexend]">
          <Form.Group className="tw-w-full">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t("MacBotResponseForm/key1")}
            </Form.Label>
            <Form.Control
              max={30}
              maxLength={30}
              {...register("BlockTitle")}
              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            />
            <HintBlock texts={["MacBotResponseForm/maxlenght"]} />
            <Form.Control.Feedback
              type="invalid"
              className="d-block font-poppins"
            >
              {errors.BlockTitle?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="tw-w-full ">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t("MacBotResponseForm/key2")}
            </Form.Label>

            <ScenarioSimpleTextInput
              isMultiline={true}
              placeholder={t("MacBotResponseForm/l-04")}
              setValue={(text) => {
                setValue("BotResponse", text);
              }}
              value={getValues("BotResponse")}
              contextMenuList={contextMenuList}
              isInvalid={!!errors.BotResponse}
            />
            <Form.Control.Feedback
              type="invalid"
              className="d-block font-poppins"
            >
              {errors.BotResponse?.message}
            </Form.Control.Feedback>
            <div>
              <button
                type="button"
                className="tw-bg-transparent tw-text-sm tw-font-normal tw-font-['Lexend'] tw-underline tw-leading-tight tw-border-none"
                onClick={() => setShowMarkDownGuide(true)}
              >
                <BiBrush className="tw-mr-2" color="blue" />
                <HintBlock texts={["Markdown/tooltips"]} />
              </button>
            </div>
          </Form.Group>

          <div className=" tw-mt-5 tw-self-stretch tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
            <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
              {t("DashboardSettingCustomizeChat/preview")}
            </div>
            <div
              className={`tw-p-0.5 tw-bg-white tw-rounded-tl-2xl tw-rounded-tr-2xl tw-rounded-br-2xl tw-flex-col tw-justify-center tw-items-start tw-flex`}
            >
              <div className="tw-px-4 tw-py-3 tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-flex">
                <ReactMarkdown
                  remarkPlugins={[
                    remarkGfm as Pluggable,
                    [remarkEmoji, { emoticon: false }] as Pluggable,
                    remarkBreaks
                  ]}
                  components={markdownComponents}
                  className={"tw-text-gray-600 tw-px-2"}
                >
                  {watch("BotResponse")}
                </ReactMarkdown>
              </div>
            </div>
          </div>
          <AiQC setValue={setValue} watch={watch} name="isAiQcActive" />
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {showDeleteButton && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && (
              <NodeParentChanger tree={tree} currentId={itemData.id} />
            )}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              type="submit"
              className="tw-h-11 tw-px-5 tw-py-3 tw-bg-primary tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex"
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("MacBotResponseForm/btn-02")
              )}
            </Button>
          </div>
        </div>

        <NoteForm
          noteFormMethods={{ register, watch, errors, fields, setValue }}
        />
      </ScenarioSide>

      <MacBotButtonSettingForm
        show={showButtonSetting}
        closeModal={closeButtonSettingModal}
        addQuickReplies={addButtonHandler}
        editQuickReplies={editButtonHandler}
        selectedButtonToEdit={selectedButtonToEdit}
        quickReplies={quickReplies}
        removeButton={removeButtonHandler}
        tree={tree}
      />

      <Modal
        show={showMarkDownGuide}
        onHide={() => setShowMarkDownGuide(false)}
        size="lg"
        centered
      >
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-justify-between tw-items-center tw-pb-4">
                <MarkdownHelpModal />
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

const VariableMenu = ({
  contextMenuList,
  back,
  onSelect
}: {
  contextMenuList: any[];
  back: () => void;
  onSelect: (context: any) => void;
}) => {
  return (
    <div className="tw-w-[342px] tw-h-[281px] tw-relative tw-z-50 ">
      <div className="tw-w-[342px] tw-h-[272px] tw-py-2 tw-left-0 tw-top-[9px] tw-absolute tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
        <div className="tw-pl-6 tw-pr-2 tw-pt-2 tw-pb-1 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
          <div className="tw-w-[228px] tw-h-5 tw-text-gray-400 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            <BiArrowBack
              onClick={back}
              className="tw-cursor-pointer tw-h-5 tw-w-5"
            />
            <span> Variables</span>
          </div>
        </div>
        <div className="tw-self-stretch tw-h-[220px] tw-px-2 tw-flex-col tw-justify-start tw-items-start tw-flex tw-overflow-hidden tw-overflow-y-scroll">
          {contextMenuList?.map((dr, index) => (
            <div
              key={index}
              onClick={() => {
                onSelect(dr);
              }}
              className="tw-cursor-pointer tw-self-stretch tw-px-4 tw-py-2.5 hover:tw-bg-gray-100 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
            >
              <BiSolidShapes className="tw-w-6 tw-h-6 tw-relative" />
              <div className="tw-w-full tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {dr.name?.length > 25
                  ? dr.name.substring(0, 25) + " ..."
                  : dr.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ActionsMenu = ({
  contextMenuList,
  onSelect
}: {
  contextMenuList: any[];
  onSelect: (context: any) => void;
}) => {
  const [pages, setPages] = useState("ActionMenu");

  return (
    <>
      {pages == "ActionMenu" && (
        <div className="tw-w-[342px] tw-h-[281px] tw-relative tw-z-50">
          <div className="tw-w-[342px] tw-h-[272px] tw-py-2 tw-left-0 tw-top-[9px] tw-absolute tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
            <div className="tw-pl-6 tw-pr-2 tw-pt-2 tw-pb-1 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
              <div className="tw-w-[228px] tw-h-5 tw-text-gray-400 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                Actions
              </div>
            </div>
            <div className="tw-self-stretch tw-h-[220px] tw-px-2 tw-flex-col tw-justify-start tw-items-start tw-flex">
              <div
                onClick={() => {
                  setPages("VariableMenu");
                }}
                className="tw-cursor-pointer tw-self-stretch tw-px-4 tw-py-2.5 hover:tw-bg-gray-100 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
              >
                <BiSolidShapes className="tw-w-6 tw-h-6 tw-relative" />
                <div className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                  Variables
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {pages == "VariableMenu" && (
        <>
          <VariableMenu
            contextMenuList={contextMenuList}
            back={() => {
              setPages("ActionMenu");
            }}
            onSelect={onSelect}
          />
        </>
      )}
    </>
  );
};

const markdownComponents = {
  code({ inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || "");
    return !inline && match ? (
      <SyntaxHighlighter
        style={dracula}
        language={match[1]}
        PreTag="div"
        className="tw-rounded-lg tw-overflow-x-auto tw-p-2"
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code className="tw-bg-gray-100 tw-text-black tw-rounded tw-p-1 tw-font-mono tw-text-sm">
        {children}
      </code>
    );
  },

  pre({ children }: any) {
    return <pre className="tw-overflow-x-auto tw-my-4">{children}</pre>;
  },

  // Headings
  h1: ({ children }: any) => (
    <h1 className="tw-text-3xl tw-font-bold tw-mb-3">{children}</h1>
  ),
  h2: ({ children }: any) => (
    <h2 className="tw-text-2xl tw-font-bold tw-mb-2">{children}</h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="tw-text-xl tw-font-bold tw-mb-1">{children}</h3>
  ),
  h4: ({ children }: any) => (
    <h4 className="tw-text-lg tw-font-bold tw-mb-1">{children}</h4>
  ),
  h5: ({ children }: any) => <h5 className="tw-text-sm ">{children}</h5>,
  h6: ({ children }: any) => <h6 className="tw-text-xs ">{children}</h6>,

  // Paragraphs and text formatting
  p: ({ children }: any) => <p className="tw-leading-relaxed">{children}</p>,

  strong: ({ children }: any) => (
    <strong className="tw-font-bold">{children}</strong>
  ),

  em: ({ children }: any) => <em className="tw-italic">{children}</em>,

  // Lists
  ul: ({ children }: any) => (
    <ul className="tw-list-disc tw-pl-4 tw-mb-2">{children}</ul>
  ),
  ol: ({ children }: any) => (
    <ol className="tw-list-decimal tw-pl-4 tw-mb-2">{children}</ol>
  ),
  li: ({ children }: any) => <li className="tw-mb-1 tw-pl-2">{children}</li>,

  // Blockquotes
  blockquote: ({ children }: any) => (
    <blockquote className="tw-border-l-4 tw-border-gray-300 tw-pl-4 tw-italic tw-my-4">
      {children}
    </blockquote>
  ),

  // Links
  a: ({ href, children }: any) => (
    <a
      href={href}
      className="tw-hover:text-blue-800 tw-underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),

  // Tables
  table: ({ children }: any) => (
    <div className="tw-overflow-x-auto tw-my-4">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="tw-bg-gray-50">{children}</thead>
  ),
  tbody: ({ children }: any) => (
    <tbody className="tw-divide-y tw-divide-gray-200">{children}</tbody>
  ),
  tr: ({ children }: any) => <tr>{children}</tr>,

  td: ({ children }: any) => (
    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">{children}</td>
  ),
  th: ({ children }: any) => (
    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
      {children}
    </th>
  ),

  // Horizontal rule
  hr: () => <hr className="tw-my-4 tw-border-t tw-border-gray-300" />,

  // Images
  img: ({ src, alt }: any) => (
    <img
      src={src}
      alt={alt}
      className="tw-max-w-full tw-h-auto tw-rounded-lg tw-my-4"
    />
  )
};
